// Vendor
import { useRoute } from "vue-router";

// App
import { useAsyncData, useState } from "#app";

export default async function useLocalizedRouteParams(
  contentfulId?: MaybeRef<string>,
) {
  const { availableLocales, locale } = useI18n();
  const { params: routeParams } = useRoute();

  const continents = useState<ILocalizedRegion[] | undefined>(
    "continent-slugs",
    () => ref(),
  );

  const cities = useState<ILocalizedRegion[] | undefined>("city-slugs", () =>
    ref(),
  );

  const slugParams = useState<
    Partial<Record<AppLocale, string>> | undefined | null
  >("localized-slug-params", () => ref());

  const currentContinent = computed(() =>
    continents.value?.find(
      (continent) =>
        continent.slug[locale.value as AppLocale] === routeParams.continent,
    ),
  );

  const currentCity = computed(() =>
    cities.value?.find(
      (city) => city.slug[locale.value as AppLocale] === routeParams.city,
    ),
  );

  const { data: regionSlugs, execute: fetchRegionSlugs } = useAsyncData(
    "localized-region-slugs",
    async () => {
      const [continents, cities] = await Promise.all([
        $fetch<CfContinentSkeleton["fields"][]>("/api/v2/continents"),
        $fetch<CfContinentSkeleton["fields"][]>("/api/v2/cities"),
      ]);

      return {
        continents: continents.map<ILocalizedRegion>(({ code, slug }) => ({
          code: code.en,
          slug: Object.entries(slug).reduce(
            (acc, [locale, slug]) => ({
              ...(acc || {}),
              [getLocaleKeyByValue(locale)]: slug,
            }),
            {},
          ),
        })),
        cities: cities.map<ILocalizedRegion>(({ code, slug }) => ({
          code: code.en,
          slug: Object.entries(slug).reduce(
            (acc, [locale, slug]) => ({
              ...(acc || {}),
              [getLocaleKeyByValue(locale)]: slug,
            }),
            {},
          ),
        })),
      };
    },
    {
      dedupe: "defer",
      immediate: false,
    },
  );

  if (contentfulId) {
    const localizedSlugs = await useLocalizedSlugs(contentfulId);

    slugParams.value = localizedSlugs?.data.value;
  }

  if (
    ("continent" in routeParams && !continents.value) ||
    ("city" in routeParams && !cities.value)
  ) {
    await fetchRegionSlugs();

    if (regionSlugs.value?.continents) {
      continents.value = regionSlugs.value.continents;
    }

    if (regionSlugs.value?.cities) {
      cities.value = regionSlugs.value.cities;
    }
  }

  const localizedSlugParams = computed<
    Partial<Record<AppLocale, Record<string, string>>>
  >(() =>
    availableLocales.reduce(
      (acc, localeKey) => ({
        ...acc,
        [localeKey as AppLocale]: {
          ...(currentContinent.value && "continent" in routeParams
            ? {
                continent: currentContinent.value.slug[localeKey as AppLocale],
              }
            : {}),
          ...(currentCity.value && "city" in routeParams
            ? { city: currentCity.value.slug[localeKey as AppLocale] }
            : {}),
          ...(slugParams.value &&
          ("slug" in routeParams || "hotelSlug" in routeParams)
            ? {
                ["hotelSlug" in routeParams ? "hotelSlug" : "slug"]:
                  slugParams.value?.[localeKey as AppLocale] ||
                  slugParams.value?.en,
              }
            : {}),
        },
      }),
      {},
    ),
  );

  return localizedSlugParams;
}
